<template>
  <div class="domain-list">
    <a-spin :spinning="fetchingDomains">
      <DynamicScroller
        class="domain-scroller"
        :items="normalizedDomains"
        :min-item-size="160"
        :key-field="'__rowKey'"
      >
        <template v-slot="{ item: domain, index, active }">
          <DynamicScrollerItem
            :item="domain"
            :active="active"
            :size-dependencies="[
              getIsSelected(domain),
              getIsLoading(domain),
              getHasNotes(domain),
              getStatus(domain),
              getHasCert(domain),
              getIsForceHttps(domain),
            ]"
            :data-index="index"
          >
            <a-card :body-style="{ padding: 0 }" class="w-100">
              <DomainListItem
                :domain="domain"
                :is-free-cname="isFreeCname"
                :is-selected="getIsSelected(domain)"
                @select="$emit('select', $event)"
              />
              <div v-if="getIsLoading(domain)" class="loading" />
            </a-card>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
    </a-spin>

    <div class="domain-pagination">
      <a-pagination
        :disabled="fetchingDomains"
        :current="currentPage"
        :page-size="domainPerPage"
        :total="totalDomainsToPaginate"
        @change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import DomainListItem from '@/views/domain/DomainListItem.vue';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';

export default {
  components: { DomainListItem, DynamicScroller, DynamicScrollerItem },
  props: [
    'fetchingDomains',
    'domains',
    'selectedRowKeyMap',
    'isFreeCname',
    'currentPage',
    'domainPerPage',
    'totalDomainsToPaginate',
  ],
  computed: {
    normalizedDomains() {
      // vue-virtual-scroller expects a stable key field for each item.
      // We attach a non-reactive-ish helper field; it won’t be sent to API.
      return (this.domains || []).map((d) => {
        const nextKey = d ? this.getRowKey(d) : '';
        if (d && d.__rowKey === nextKey) return d;
        if (!d) return d;
        d.__rowKey = nextKey;
        return d;
      });
    }
  },
  data() {
    return {};
  },
  methods: {
    getDomainId(domain) {
      return (domain && (domain.getId ? domain.getId() : (domain._data && domain._data.id))) || domain?.id;
    },
    getRowKey(domain) {
      return domain?.getMainHost() || domain?.domain_name || domain?.name || domain?.id;
    },
    getIsSelected(domain) {
      const key = this.getRowKey(domain);
      return Boolean(this.selectedRowKeyMap && this.selectedRowKeyMap[key]);
    },
    getIsLoading(domain) {
      const id = this.getDomainId(domain);
      return this.$store.getters.isDomainLoading(id);
    },
    getStatus(domain) {
      return (domain?.getStatus && domain.getStatus()) || domain?._data?.status || '';
    },
    getHasNotes(domain) {
      const note = domain && (domain.getDescription ? domain.getDescription() : (domain._data && domain._data.description));
      return Boolean(note && note.length > 0 && !['', '-'].includes(String(note).trim()));
    },
    getHasCert(domain) {
      return Boolean(domain?.hasCert?.());
    },
    getIsForceHttps(domain) {
      return Boolean(domain?.isForceHttps?.());
    },
    handlePageChange(page) {
      this.$emit('pageChanged', page);
    }
  }
};
</script>

<style>
.domain-list .ant-list-pagination {
  text-align: center;
}

.domain-scroller {
  /* allow scroller to create its own internal scroll container */
  position: relative;
  max-height: 70vh;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}

.domain-pagination {
  display: flex;
  justify-content: center;
  padding-top: 12px;
}

.domain-list .ant-list-item .ant-card {
  position: relative;
}

.domain-list .loading {
  position: absolute;
  inset: 0;
  border-radius: 6px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(248, 250, 252, 0.5) 50%,
    rgba(255, 255, 255, 0.3) 100%
  );
  backdrop-filter: blur(1px);
  pointer-events: auto;
  user-select: none;
  cursor: progress;
  overflow: hidden;
}

.domain-list .loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 40%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(0, 175, 238, 0.15) 50%,
    transparent 100%
  );
  animation: pulse-sweep 1.8s ease-in-out infinite;
}

@keyframes pulse-sweep {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
</style>